import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  Warehouse, 
  FlaskConical, 
  Pill, 
  Stethoscope, 
  FileText, 
  Accessibility, 
  Cross, 
  Microscope,
  ArrowRight,
  ArrowLeft 
} from 'lucide-react';

const iconMap = {
  Warehouse,
  FlaskConical,
  Pill,
  Stethoscope,
  FileText,
  Accessibility,
  Cross,
  Microscope,
};

const ServicesSection = () => {
  const { t, isRTL } = useLanguage();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  // Services with their target sections for "Learn More"
  const services = [
    { id: 1, title: t('services.s1.title'), description: t('services.s1.desc'), icon: 'Warehouse', targetSection: '#contact' },
    { id: 2, title: t('services.s2.title'), description: t('services.s2.desc'), icon: 'FlaskConical', targetSection: '#contact' },
    { id: 3, title: t('services.s3.title'), description: t('services.s3.desc'), icon: 'Pill', targetSection: '#contact' },
    { id: 4, title: t('services.s4.title'), description: t('services.s4.desc'), icon: 'Stethoscope', targetSection: '#products' },
    { id: 5, title: t('services.s5.title'), description: t('services.s5.desc'), icon: 'FileText', targetSection: '#contact' },
    { id: 6, title: t('services.s6.title'), description: t('services.s6.desc'), icon: 'Accessibility', targetSection: '#products' },
    { id: 7, title: t('services.s7.title'), description: t('services.s7.desc'), icon: 'Cross', targetSection: '#contact' },
    { id: 8, title: t('services.s8.title'), description: t('services.s8.desc'), icon: 'Microscope', targetSection: '#products' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className={`absolute top-20 ${isRTL ? 'right-20' : 'left-20'} w-64 h-64 bg-blue-100 rounded-full blur-3xl`}></div>
        <div className={`absolute bottom-20 ${isRTL ? 'left-20' : 'right-20'} w-80 h-80 bg-red-100 rounded-full blur-3xl`}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-blue-50 text-brand-blue text-sm font-semibold rounded-full mb-4">
            {t('services.badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            {t('services.title1')}
            <span className="text-brand-red"> {t('services.title2')}</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('services.description')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div
                key={service.id}
                onClick={() => scrollToSection(service.targetSection)}
                className="group bg-white p-6 rounded-2xl border border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-xl hover:shadow-blue-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  {IconComponent && <IconComponent className="w-7 h-7 text-brand-blue" />}
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3 group-hover:text-brand-blue transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  {service.description}
                </p>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollToSection(service.targetSection);
                  }}
                  className={`flex items-center text-brand-red text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <span>{t('services.learnMore')}</span>
                  <ArrowIcon className={`w-4 h-4 ${isRTL ? 'mr-1 group-hover:-translate-x-1' : 'ml-1 group-hover:translate-x-1'} transition-transform`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
